import React, { useCallback, useState, useRef, useEffect } from "react";
import { useDropzone, FileRejection } from "react-dropzone";
import Webcam from "react-webcam";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Drawer,
  IconButton,
  ImageListItemBar,
  Stack,
  Typography,
  InputLabel,
} from "@mui/material";
import { FaFileUpload } from "@react-icons/all-files/fa/FaFileUpload";
import { GrDocumentUpload } from "@react-icons/all-files/gr/GrDocumentUpload";
import { FaCamera } from "@react-icons/all-files/fa/FaCamera";
import { FaTrash } from "@react-icons/all-files/fa/FaTrash";
import { useTranslation } from "react-i18next";
import { RedAsterisk } from "../../../styles/controls"; 
import MessageViewer from "@shared/messageViewer";
import theme from "config/theme";
import {
  computeLaplacianVariance,
  GALLERY_MAX_SIZE,
  CAMERA_MAX_SIZE,
  BLUR_THRESHOLD,
  COUNTDOWN_SECONDS,
  WEBCAM_CAPTURE_SIZE,
  WEBCAM_VIDEO_CONSTRAINTS,
} from "./imageUtils";

interface FileUploaderWithCameraProps {
  onChange: (file: File | null) => void;
  value: File | string | null;
  label?: string;
  error?: string;
}

const FileUploaderWithCamera: React.FC<FileUploaderWithCameraProps> = ({
  onChange,
  value,
  label,
  error,
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const webcamRef = useRef<Webcam>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [dropzoneError, setDropzoneError] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [blurWarning, setBlurWarning] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (value) {
      if (typeof value === 'string') {
        setPreview(value);
      } else {
        const objectUrl = URL.createObjectURL(value);
        setPreview(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
      }
    } else {
      setPreview(null);
    }
  }, [value]);

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      setDropzoneError(null);
      setCameraError(null);
      console.log('onDrop triggered. Files:', acceptedFiles);

      if (fileRejections.length > 0) {
        const firstError = fileRejections[0].errors[0];
        if (firstError.code === "file-too-large") {
          setDropzoneError("File is larger than 1MB. Please upload a smaller file.");
        } else if (firstError.code === "file-invalid-type") {
          setDropzoneError("Invalid file type. Please upload a JPG or PNG file.");
        } else {
          setDropzoneError(firstError.message);
        }
      } else if (acceptedFiles.length > 0) {
        onChange(acceptedFiles[0]);
      }
      setIsModalOpen(false); // Close modal after selection/rejection
    },
    [onChange]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { "image/jpeg": [".jpeg", ".jpg"], "image/png": [".png"] },
    multiple: false,
    maxSize: GALLERY_MAX_SIZE, // 1MB for gallery uploads
  });

  const handleCapture = useCallback(async () => {
    setBlurWarning(null);
    const imageSrc = webcamRef.current?.getScreenshot();
    if (!imageSrc) return;

    const image = new Image();
    image.src = imageSrc;
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Keep original image orientation without rotation
      canvas.width = image.width;
      canvas.height = image.height;
      ctx.drawImage(image, 0, 0);

      // Sharpness check — reject blurry images
      const sharpness = computeLaplacianVariance(canvas);
      if (sharpness < BLUR_THRESHOLD) {
        setBlurWarning("Image appears blurry — please hold steady and try again.");
        setIsCapturing(false);
        return; // Keep camera open for retry
      }

      canvas.toBlob((blob) => {
        if (!blob) {
          setIsCapturing(false);
          return;
        }

        // Camera file size check (5MB limit)
        if (blob.size > CAMERA_MAX_SIZE) {
          setCameraError("Captured image is too large. Please try again.");
          setIsCapturing(false);
          return;
        }

        const file = new File([blob], "capture.png", { type: "image/png" });
        onChange(file);
        setIsCameraOpen(false);
        setIsModalOpen(false);
        setIsCapturing(false);
      }, 'image/png');
    }
  }, [webcamRef, onChange]);

  const startCaptureCountdown = useCallback(() => {
    setBlurWarning(null);
    setIsCapturing(true);
    setCountdown(COUNTDOWN_SECONDS);

    let remaining = COUNTDOWN_SECONDS;
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        if (countdownRef.current) clearInterval(countdownRef.current);
        setCountdown(null);
        handleCapture();
      } else {
        setCountdown(remaining);
      }
    }, 1000);
  }, [handleCapture]);

  const openCamera = () => {
    setCameraError(null);
    setBlurWarning(null);
    setIsCameraOpen(true);
  };
  const closeCamera = () => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setCountdown(null);
    setIsCapturing(false);
    setBlurWarning(null);
    setIsCameraOpen(false);
  };

  const handleCameraError = (error: any) => {
    if (error.name === 'NotAllowedError') {
      setCameraError("Camera access is required. Please grant camera permission in your browser settings and try again.");
    } else {
      setCameraError(`An error occurred while accessing the camera: ${error.message}`);
    }
    closeCamera();
  };

  const removeFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDropzoneError(null);
    setCameraError(null);
    onChange(null);
  };

  return (
    <div>
      {label && (
        <>
          <InputLabel>
            {label}
            <RedAsterisk>&thinsp;*</RedAsterisk>
          </InputLabel>
          <Typography variant="body2" color={"#333"}>{"The image must clearly show the account holder name, account number, and IFSC code."}</Typography>
        </>
      )}
      <Box
        onClick={() => setIsModalOpen(true)}
        sx={{
          position: "relative",
          border: `2px dashed ${theme.palette.primary.main}`,
          borderRadius: 1,
          p: 2,
          textAlign: "center",
          cursor: "pointer",
          backgroundColor: "rgba(0, 124, 191, 0.08)",
          minHeight: "150px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundImage: preview ? `url(${preview})` : "none",
        }}
      >
        {/* This Box is now just a clickable display area */}
        {value && preview ? (
          <ImageListItemBar
            sx={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}
            title={typeof value === 'string' ? value.split('/').pop() : value.name}
            actionIcon={<IconButton sx={{ color: "red", background:'#fff' }} onClick={removeFile}><FaTrash size={15} /></IconButton>}
          />
        ) : (
          <Stack alignItems="center" spacing={1}>
            <style>
              {`
                svg path {
                  stroke: ${theme.palette.primary.main}
                }
                `}
            </style>
            <GrDocumentUpload size={24} />
            
            <Typography>
              {t("drag_drop_file", {
                defaultValue: "Click here to upload",
              })}
            </Typography>
            <Typography variant="body2" color={"#333"}>JPG, PNG (max. 1 MB)</Typography>
          </Stack>
        )}
      </Box>
      {(error || dropzoneError || cameraError) && <MessageViewer status="error" details={error || dropzoneError || cameraError || ''} />}

      {/* Modal for upload options */}
      <Drawer
        anchor="bottom"
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16 } }}
      >
        <Stack p={2} spacing={2}>
          <Typography variant="h6" textAlign="center">
            {t("upload_cheque", { defaultValue: "Upload cancelled cheque or passbook photo" })}
          </Typography>
          {/* The getInputProps are now on a hidden input, triggered by the button */}
          <input {...getInputProps()} />
          <Button
            {...getRootProps()}
            variant="outlined"
            startIcon={<FaFileUpload />}
            size="large"
          >
            {t("open_gallery", { defaultValue: "Open Gallery" })}
          </Button>
          <Button
            variant="outlined"
            startIcon={<FaCamera />}
            onClick={openCamera}
            size="large"
          >
            {t("open_camera", { defaultValue: "Open Camera" })}
          </Button>
        </Stack>
      </Drawer>

      <Dialog open={isCameraOpen} onClose={closeCamera} fullScreen>
        <DialogContent sx={{ p: 0, m: 0, position: "relative" }}>
          <Webcam
            audio={false}
            ref={webcamRef}
            onUserMediaError={handleCameraError}
            screenshotFormat="image/png"
            screenshotQuality={1}
            forceScreenshotSourceSize
            minScreenshotWidth={WEBCAM_CAPTURE_SIZE.width}
            minScreenshotHeight={WEBCAM_CAPTURE_SIZE.height}
            imageSmoothing={false}
            videoConstraints={WEBCAM_VIDEO_CONSTRAINTS}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />

          {/* Document alignment overlay */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* Dark mask with transparent cutout using box-shadow */}
            <Box
              sx={{
                position: "relative",
                width: "85%",
                aspectRatio: "3 / 2",
                maxHeight: "60%",
                border: "2px dashed rgba(255, 255, 255, 0.8)",
                borderRadius: "8px",
                boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.5)",
              }}
            >
              {/* Corner markers */}
              {/* Top-left */}
              <Box sx={{ position: "absolute", top: -2, left: -2, width: 24, height: 24, borderTop: "4px solid #fff", borderLeft: "4px solid #fff", borderTopLeftRadius: 8 }} />
              {/* Top-right */}
              <Box sx={{ position: "absolute", top: -2, right: -2, width: 24, height: 24, borderTop: "4px solid #fff", borderRight: "4px solid #fff", borderTopRightRadius: 8 }} />
              {/* Bottom-left */}
              <Box sx={{ position: "absolute", bottom: -2, left: -2, width: 24, height: 24, borderBottom: "4px solid #fff", borderLeft: "4px solid #fff", borderBottomLeftRadius: 8 }} />
              {/* Bottom-right */}
              <Box sx={{ position: "absolute", bottom: -2, right: -2, width: 24, height: 24, borderBottom: "4px solid #fff", borderRight: "4px solid #fff", borderBottomRightRadius: 8 }} />
            </Box>
            {/* Guide text */}
            <Typography
              sx={{
                color: "#fff",
                mt: 2,
                textAlign: "center",
                textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                fontSize: "0.875rem",
              }}
            >
              Align document within the frame
            </Typography>
          </Box>

          {/* Countdown overlay */}
          {countdown !== null && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <Typography
                sx={{
                  fontSize: "6rem",
                  fontWeight: 700,
                  color: "#fff",
                  textShadow: "0 2px 12px rgba(0,0,0,0.7)",
                }}
              >
                {countdown}
              </Typography>
            </Box>
          )}

          {/* Blur warning inside camera dialog */}
          {blurWarning && (
            <Box sx={{ position: "absolute", bottom: 80, left: 16, right: 16 }}>
              <MessageViewer status="error" details={blurWarning} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCamera}>
            {t("cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            onClick={startCaptureCountdown}
            variant="contained"
            disabled={isCapturing}
          >
            {isCapturing
              ? t("capturing", { defaultValue: "Capturing..." })
              : t("capture", { defaultValue: "Capture" })}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FileUploaderWithCamera;
